import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  MapPin, Ruler, Home as HomeIcon, Building2, Briefcase, Car, Sparkles, TrendingUp,
  ArrowRight, ArrowLeft, Loader2, CheckCircle2, MessageCircle, Bed, Bath, Wrench, Plus, Minus,
  User, Mail, Phone, FileText,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import CityAutocomplete from '../components/CityAutocomplete';
import { Button } from '../components/ui/button';
import { api, friendlyError } from '../lib/api';
import { track, EventTypes, getSessionId } from '../lib/analytics';
import { useAuth } from '../context/AuthContext';

const fmtCOP = (v) => (v ? '$' + Number(v).toLocaleString('es-CO') : '');

// Lead de valoración ya identificado en la sesión (regla del silencio):
// una vez capturado, no se vuelve a pedir el formulario y se continúa directo.
const VAL_LEAD_KEY = 'habita_valuation_lead';
function getValuationLead() {
  try { return JSON.parse(sessionStorage.getItem(VAL_LEAD_KEY) || 'null'); } catch { return null; }
}
function setValuationLead(info) {
  try { sessionStorage.setItem(VAL_LEAD_KEY, JSON.stringify(info)); } catch { /* noop */ }
}

// Nomenclatura colombiana: siempre en millones ('M'), nunca 'B' (billions).
// Ej.: 1.200.000.000 → '$1.200M'. Redondeo al millón más cercano.
const compact = (v) => {
  if (!v) return '';
  return '$' + Math.round(v / 1_000_000).toLocaleString('es-CO') + 'M';
};

const TYPES = [
  { value: 'apartamento', label: 'Apartamento', icon: Building2 },
  { value: 'casa', label: 'Casa', icon: HomeIcon },
  { value: 'oficina', label: 'Oficina', icon: Briefcase },
];
const CONDITIONS = [
  { value: 'nuevo', label: 'Nuevo / Estrenar', desc: 'A estrenar, sin habitar' },
  { value: 'remodelado', label: 'Remodelado', desc: 'Renovado recientemente' },
  { value: 'bueno', label: 'Buen estado', desc: 'Bien conservado' },
  { value: 'original', label: 'Original', desc: 'Sin remodelar, habitable' },
  { value: 'para_remodelar', label: 'Para remodelar', desc: 'Necesita arreglos' },
];

export default function ValoraGratis() {
  const [stage, setStage] = useState('location'); // location | initial | questions | final
  const [form, setForm] = useState({ city: '', neighborhood: '', area: '', stratum: '' });
  const [details, setDetails] = useState({ property_type: '', beds: 2, baths: 2, parking: null, condition: '' });
  const [qStep, setQStep] = useState(0);
  const [initialEst, setInitialEst] = useState(null);
  const [finalResult, setFinalResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sellerLeadId, setSellerLeadId] = useState(null);
  const [identified, setIdentified] = useState(!!getValuationLead());

  const setD = (k, v) => setDetails((d) => ({ ...d, [k]: v }));

  const goToRefinement = () => { setStage('questions'); setQStep(0); };

  // Captura del rango inicial: es el ÚNICO camino para continuar al refinamiento.
  // Al confirmar valuationCapture, guardamos la identidad en sesión, mostramos
  // un éxito breve y redirigimos automáticamente al refinamiento.
  const handleValuationCaptured = (info) => {
    setValuationLead(info);
    setIdentified(true);
    setTimeout(() => { goToRefinement(); }, 1200);
  };

  // Crea (perezosamente) un seller_lead solo cuando el usuario envía la captura
  // en la etapa inicial, sin obligarlo a completar el refinamiento. Se cachea
  // para reutilizarlo. En la etapa final se usa el lead_id del resultado.
  const ensureSellerLead = async () => {
    if (sellerLeadId) return sellerLeadId;
    const res = await api.createSellerLead({
      city: form.city.trim(), neighborhood: form.neighborhood.trim(),
      area: parseInt(form.area, 10), stratum: parseInt(form.stratum, 10),
    });
    setSellerLeadId(res.lead_id);
    return res.lead_id;
  };

  const locationValid =
    form.city.trim().length >= 2 && form.neighborhood.trim().length >= 2 &&
    Number(form.area) >= 10 && Number(form.stratum) >= 1 && Number(form.stratum) <= 6;

  // STEP 1 → initial estimate (venta + arriendo en paralelo, mismo motor)
  const getInitial = async (e) => {
    e?.preventDefault();
    if (!locationValid || loading) return;
    setError(''); setLoading(true);
    try {
      const common = {
        city: form.city.trim(), neighborhood: form.neighborhood.trim(),
        area: parseInt(form.area, 10), stratum: parseInt(form.stratum, 10),
      };
      const [sale, rent] = await Promise.all([
        api.estimatePricing({ ...common, operation: 'venta' }),
        api.estimatePricing({ ...common, operation: 'arriendo' }),
      ]);
      setInitialEst({ sale, rent });
      setStage('initial');
      try { track(EventTypes.VALUATION_STARTED, { city: common.city, neighborhood: common.neighborhood, stratum: common.stratum, area: common.area }); } catch { /* noop */ }
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(friendlyError(err));
    } finally { setLoading(false); }
  };

  // STEP 4 → refined estimate + save lead
  const getFinal = async () => {
    setError(''); setLoading(true);
    try {
      const res = await api.createSellerLead({
        city: form.city.trim(), neighborhood: form.neighborhood.trim(), area: parseInt(form.area, 10),
        stratum: parseInt(form.stratum, 10),
        property_type: details.property_type || null,
        beds: details.beds, baths: details.baths,
        parking: details.parking, condition: details.condition || null,
      });
      setFinalResult(res);
      setStage('final');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(friendlyError(err));
    } finally { setLoading(false); }
  };

  const restart = () => {
    setStage('location'); setForm({ city: '', neighborhood: '', area: '', stratum: '' });
    setDetails({ property_type: '', beds: 2, baths: 2, parking: null, condition: '' });
    setQStep(0); setInitialEst(null); setFinalResult(null); setError(''); setSellerLeadId(null);
  };

  return (
    <div className="min-h-screen bg-habita-cream" translate="no">
      <SEO
        title="Valora tu inmueble gratis · habita.co"
        description="Descubre en segundos el precio de venta real de tu casa o apartamento en Colombia. Valoración gratuita con IA y datos reales del mercado."
      />
      <Header />

      {/* HERO — light, high contrast */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#E8F3EC] via-[#FAF7F2] to-white" />
        <div className="relative max-w-6xl mx-auto px-4 lg:px-8 py-14 lg:py-20 text-habita-dark">
          <span className="inline-block px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold uppercase tracking-widest">
            Valoración gratuita con IA
          </span>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-black mt-5 leading-tight text-habita-dark">
            ¿Cuánto vale tu<br />inmueble hoy?
          </h1>
          <p className="text-lg lg:text-xl text-gray-700 mt-5 max-w-2xl font-medium">
            Obtén tu precio real aquí mismo: primero un estimado por tu sector y luego un valor ajustado según tu inmueble. Gratis y sin salir de la página.
          </p>
        </div>
      </section>

      <main className="max-w-6xl mx-auto px-4 lg:px-8 -mt-8 lg:-mt-12 pb-20 relative z-10">
        <div className="grid lg:grid-cols-5 gap-8 items-start">
          <div className="lg:col-span-3">
            <div className="bg-white rounded-2xl shadow-xl border border-black/5 p-6 lg:p-8" data-testid="valora-card">
              {stage === 'location' && (
                <LocationStep form={form} setForm={setForm} valid={locationValid} loading={loading}
                  error={error} onSubmit={getInitial} />
              )}
              {stage === 'initial' && (
                <InitialResult est={initialEst?.sale} rent={initialEst?.rent} form={form}
                  resolveSellerLeadId={ensureSellerLead}
                  alreadyIdentified={identified}
                  onCaptured={handleValuationCaptured}
                  onContinue={goToRefinement} />
              )}
              {stage === 'questions' && (
                <Questionnaire details={details} setD={setD} qStep={qStep} setQStep={setQStep}
                  loading={loading} error={error} onFinish={getFinal} />
              )}
              {stage === 'final' && (
                <FinalResult result={finalResult} form={form} details={details} identified={identified} onRestart={restart} />
              )}
            </div>
          </div>

          {/* Benefits sidebar */}
          <aside className="lg:col-span-2 lg:pt-4 space-y-5">
            <h3 className="font-serif text-xl font-bold text-habita-dark">¿Por qué valorar con Habita?</h3>
            {[
              { icon: TrendingUp, t: 'Precio real, no un rango vago', d: 'Ajustamos el valor según el estado, parqueadero y características de tu inmueble.' },
              { icon: Sparkles, t: 'IA + datos del mercado', d: 'Combinamos baselines reales de tu barrio con las particularidades de tu propiedad.' },
              { icon: MessageCircle, t: 'Acompañamiento humano', d: 'Cristian, asesor Habita, te ayuda a publicar y vender al mejor precio.' },
            ].map((b) => (
              <div key={b.t} className="flex gap-3">
                <div className="w-10 h-10 rounded-xl bg-habita-mint flex items-center justify-center shrink-0">
                  <b.icon className="w-5 h-5 text-habita-dark" />
                </div>
                <div>
                  <p className="font-semibold text-habita-dark text-sm">{b.t}</p>
                  <p className="text-sm text-gray-500 mt-0.5">{b.d}</p>
                </div>
              </div>
            ))}
          </aside>
        </div>
      </main>
      <Footer />
    </div>
  );
}

/* ---------- STEP 1: location ---------- */
function LocationStep({ form, setForm, valid, loading, error, onSubmit }) {
  return (
    <form onSubmit={onSubmit} data-testid="seller-lead-form">
      <StepBadge n={1} total={2} label="Ubicación de tu inmueble" />
      <h2 className="font-serif text-2xl font-bold text-habita-dark mt-3">¿Dónde está tu inmueble?</h2>
      <p className="text-sm text-gray-500 mt-1 mb-6">Con esto calculamos el precio de tu sector al instante.</p>

      <div className="space-y-5">
        <div>
          <Label icon={MapPin}>Ciudad</Label>
          <CityAutocomplete
            testId="seller-city-autocomplete"
            value={form.city}
            onChange={(name) => setForm((f) => ({ ...f, city: name }))}
            onSelect={(loc) => setForm((f) => ({ ...f, city: loc?.name || f.city }))}
            placeholder="Busca tu municipio (ej. Bogotá, Medellín...)"
          />
        </div>
        <Field icon={HomeIcon} label="Barrio">
          <input data-testid="seller-neighborhood-input" type="text" value={form.neighborhood}
            onChange={(e) => setForm((f) => ({ ...f, neighborhood: e.target.value }))}
            placeholder="Ej. Chicó, Laureles, Ciudad Jardín"
            className="w-full outline-none text-habita-dark placeholder:text-gray-400 bg-transparent" />
        </Field>
        <Field icon={Ruler} label="Área construida (m²)">
          <input data-testid="seller-area-input" type="number" min="10" max="5000" value={form.area}
            onChange={(e) => setForm((f) => ({ ...f, area: e.target.value }))}
            placeholder="Ej. 85"
            className="w-full outline-none text-habita-dark placeholder:text-gray-400 bg-transparent" />
        </Field>
        <div>
          <Label icon={Building2}>Estrato</Label>
          <div className="grid grid-cols-6 gap-2" data-testid="seller-stratum-group">
            {[1, 2, 3, 4, 5, 6].map((n) => (
              <button
                key={n}
                type="button"
                data-testid={`seller-stratum-${n}`}
                onClick={() => setForm((f) => ({ ...f, stratum: n }))}
                className={`h-12 rounded-xl border-2 font-bold text-base transition-all ${
                  Number(form.stratum) === n
                    ? 'border-habita bg-habita text-white'
                    : 'border-gray-200 text-habita-dark hover:border-habita'
                }`}
              >
                {n}
              </button>
            ))}
          </div>
          <p className="text-xs text-gray-400 mt-1.5">El estrato ajusta el precio base de tu sector.</p>
        </div>
      </div>

      {error && <p data-testid="seller-lead-error" className="text-sm text-red-600 mt-4 bg-red-50 rounded-lg px-3 py-2">{error}</p>}

      <Button type="submit" disabled={!valid || loading} data-testid="seller-lead-submit"
        className="w-full mt-6 bg-habita hover:bg-habita-dark text-white font-bold h-12 rounded-full text-base disabled:opacity-50">
        {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Calculando...</> : <>Ver precio estimado <ArrowRight className="w-4 h-4 ml-2" /></>}
      </Button>
    </form>
  );
}

/* ---------- STEP 2: initial result ---------- */
function InitialResult({ est, rent, form, onContinue, onCaptured, resolveSellerLeadId, alreadyIdentified }) {
  const has = est?.price_min && est?.price_max;
  const hasRent = rent?.price_min && rent?.price_max;
  return (
    <div data-testid="seller-initial-result">
      <StepBadge n={1} total={2} label="Rango inicial por tu sector" />
      <p className="text-sm text-gray-500 mt-3">Precio estimado para {form.neighborhood}, {form.city} (~{form.area} m²):</p>

      <div className="mt-4 rounded-2xl bg-gradient-to-br from-[#02543A] to-[#00824D] p-6 text-white" data-testid="seller-initial-range">
        {has ? (
          <>
            <p className="text-xs uppercase tracking-widest text-emerald-200 font-bold">Rango de precio inicial</p>
            <p className="font-serif text-3xl lg:text-4xl font-black mt-1 leading-tight">
              {compact(est.price_min)} <span className="text-white/50">–</span> {compact(est.price_max)}
            </p>
            <p className="text-white/70 text-sm mt-1">COP · estimado por datos del sector</p>
          </>
        ) : (
          <>
            <p className="font-serif text-2xl font-bold">Estamos calculando tu sector</p>
            <p className="text-white/75 text-sm mt-2">Refina con el Advisor y te damos el mejor estimado posible.</p>
          </>
        )}
      </div>

      {hasRent && (
        <div className="mt-3 rounded-xl bg-habita-mint/60 border border-emerald-200 p-4" data-testid="seller-initial-rent">
          <p className="text-xs uppercase tracking-widest text-emerald-700 font-bold">Arriendo estimado</p>
          <p className="font-serif text-xl font-bold text-habita-dark mt-0.5">
            {fmtCOP(rent.price_min)} <span className="text-gray-400">–</span> {fmtCOP(rent.price_max)} <span className="text-sm font-normal text-gray-500">/ mes</span>
          </p>
        </div>
      )}

      <div className="mt-5 rounded-xl border-2 border-emerald-600 bg-emerald-50 p-4">
        <p className="text-sm text-emerald-900 font-medium">
          Este es un rango general. Cuéntanos 4 detalles y calculamos el <strong>precio real ajustado</strong> a tu inmueble (venta y arriendo).
        </p>
      </div>

      {alreadyIdentified ? (
        // Ya identificado en la sesión → continúa directo, sin volver a pedir datos.
        <Button onClick={onContinue} data-testid="seller-continue-btn"
          className="w-full mt-4 bg-habita hover:bg-habita-dark text-white font-bold h-12 rounded-full text-base">
          <Sparkles className="w-4 h-4 mr-2" /> Continuar al precio exacto
        </Button>
      ) : (
        // La captura es el ÚNICO camino para avanzar al refinamiento del Advisor.
        <CaptureForm resolveSellerLeadId={resolveSellerLeadId} hasSale={has} hasRent={hasRent}
          submitLabel="Obtener precio más exacto y recibir mi reporte"
          onCaptured={onCaptured} />
      )}
    </div>
  );
}

/* ---------- STEP 3: questionnaire ---------- */
function Questionnaire({ details, setD, qStep, setQStep, loading, error, onFinish }) {
  const steps = ['type', 'rooms', 'parking', 'condition'];
  const canNext = {
    type: !!details.property_type,
    rooms: details.beds >= 0 && details.baths >= 0,
    parking: details.parking !== null,
    condition: !!details.condition,
  }[steps[qStep]];

  const next = () => {
    if (qStep < steps.length - 1) setQStep(qStep + 1);
    else onFinish();
  };
  const back = () => (qStep > 0 ? setQStep(qStep - 1) : null);

  return (
    <div data-testid="seller-questionnaire">
      <StepBadge n={2} total={2} label={`Detalle del inmueble · ${qStep + 1}/4`} />
      <div className="h-1.5 bg-gray-100 rounded-full mt-3 overflow-hidden">
        <div className="h-full bg-habita transition-all" style={{ width: `${((qStep + 1) / 4) * 100}%` }} data-testid="q-progress" />
      </div>

      <div className="mt-6 min-h-[220px]">
        {steps[qStep] === 'type' && (
          <QBlock title="¿Qué tipo de inmueble es?">
            <div className="grid grid-cols-3 gap-3">
              {TYPES.map((t) => (
                <button key={t.value} type="button" onClick={() => setD('property_type', t.value)}
                  data-testid={`q-type-${t.value}`}
                  className={`flex flex-col items-center gap-2 rounded-xl border-2 py-5 transition-all ${details.property_type === t.value ? 'border-habita bg-habita-mint' : 'border-gray-200 hover:border-habita'}`}>
                  <t.icon className={`w-6 h-6 ${details.property_type === t.value ? 'text-habita' : 'text-gray-500'}`} />
                  <span className="text-sm font-semibold text-habita-dark">{t.label}</span>
                </button>
              ))}
            </div>
          </QBlock>
        )}

        {steps[qStep] === 'rooms' && (
          <QBlock title="¿Cuántas habitaciones y baños tiene?">
            <Stepper label="Habitaciones" icon={Bed} value={details.beds} onChange={(v) => setD('beds', v)} min={0} max={15} testid="q-beds" />
            <Stepper label="Baños" icon={Bath} value={details.baths} onChange={(v) => setD('baths', v)} min={0} max={15} testid="q-baths" />
          </QBlock>
        )}

        {steps[qStep] === 'parking' && (
          <QBlock title="¿Tiene parqueadero?">
            <div className="grid grid-cols-2 gap-3">
              {[{ v: true, l: 'Sí, tiene' }, { v: false, l: 'No tiene' }].map((o) => (
                <button key={String(o.v)} type="button" onClick={() => setD('parking', o.v)}
                  data-testid={`q-parking-${o.v ? 'yes' : 'no'}`}
                  className={`flex items-center justify-center gap-2 rounded-xl border-2 py-6 transition-all ${details.parking === o.v ? 'border-habita bg-habita-mint' : 'border-gray-200 hover:border-habita'}`}>
                  <Car className={`w-5 h-5 ${details.parking === o.v ? 'text-habita' : 'text-gray-500'}`} />
                  <span className="text-sm font-semibold text-habita-dark">{o.l}</span>
                </button>
              ))}
            </div>
          </QBlock>
        )}

        {steps[qStep] === 'condition' && (
          <QBlock title="¿Cuál es el estado del inmueble?">
            <div className="space-y-2.5">
              {CONDITIONS.map((c) => (
                <button key={c.value} type="button" onClick={() => setD('condition', c.value)}
                  data-testid={`q-condition-${c.value}`}
                  className={`w-full flex items-center gap-3 rounded-xl border-2 p-4 text-left transition-all ${details.condition === c.value ? 'border-habita bg-habita-mint' : 'border-gray-200 hover:border-habita'}`}>
                  <Wrench className={`w-5 h-5 shrink-0 ${details.condition === c.value ? 'text-habita' : 'text-gray-400'}`} />
                  <div>
                    <p className="text-sm font-semibold text-habita-dark">{c.label}</p>
                    <p className="text-xs text-gray-500">{c.desc}</p>
                  </div>
                </button>
              ))}
            </div>
          </QBlock>
        )}
      </div>

      {error && <p data-testid="seller-lead-error" className="text-sm text-red-600 mt-2 bg-red-50 rounded-lg px-3 py-2">{error}</p>}

      <div className="flex items-center gap-3 mt-6">
        <button type="button" onClick={back} disabled={qStep === 0}
          className="flex items-center gap-1 text-sm text-gray-500 disabled:opacity-0" data-testid="q-back">
          <ArrowLeft className="w-4 h-4" /> Atrás
        </button>
        <Button onClick={next} disabled={!canNext || loading} data-testid="q-next"
          className="flex-1 bg-habita hover:bg-habita-dark text-white font-bold h-12 rounded-full text-base disabled:opacity-50">
          {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Calculando precio...</>
            : qStep < 3 ? <>Siguiente <ArrowRight className="w-4 h-4 ml-2" /></>
              : <>Ver mi precio final <Sparkles className="w-4 h-4 ml-2" /></>}
        </Button>
      </div>
    </div>
  );
}

/* ---------- STEP 4: final result + cierre ---------- */
function FinalResult({ result, form, details, identified, onRestart }) {
  const sale = result?.estimate;
  const rent = result?.rent_estimate;
  const yld = result?.gross_yield;
  const hasSale = sale?.price_min && sale?.price_max;
  const hasRent = rent?.price_min && rent?.price_max;
  const tipo = details.property_type || 'inmueble';

  useEffect(() => {
    try {
      track(EventTypes.VALUATION_RESULT_VIEWED, {
        city: form.city, neighborhood: form.neighborhood,
        property_type: details.property_type || null,
        sale_min: sale?.price_min, sale_max: sale?.price_max,
        rent_min: rent?.price_min, rent_max: rent?.price_max,
        gross_yield_pct: yld?.annual_pct, rent_source: rent?.source,
      });
    } catch { /* analytics no debe romper la vista */ }
  }, []);

  return (
    <div data-testid="seller-lead-result">
      <StepBadge n={2} total={2} label="Estimación de tu inmueble" done />
      <p className="text-sm text-gray-500 mt-3">
        {tipo.charAt(0).toUpperCase() + tipo.slice(1)} en {form.neighborhood}, {form.city} (~{form.area} m²)
      </p>

      {/* SI DECIDES VENDER */}
      <div className="mt-4 rounded-2xl bg-gradient-to-br from-[#02543A] to-[#00824D] p-6 text-white" data-testid="seller-price-range">
        <p className="text-xs uppercase tracking-widest text-emerald-200 font-bold">Si decides vender</p>
        {hasSale ? (
          <>
            <p className="font-serif text-3xl lg:text-4xl font-black mt-1 leading-tight">
              {fmtCOP(sale.price_min)} <span className="text-white/50 text-2xl">–</span> {fmtCOP(sale.price_max)}
            </p>
            <p className="text-white/70 text-sm mt-1">COP · valor de venta estimado</p>
          </>
        ) : (
          <p className="font-serif text-2xl font-bold mt-1">Un asesor te enviará el rango exacto</p>
        )}
      </div>

      {/* SI DECIDES ARRENDAR */}
      {hasRent && (
        <div className="mt-3 rounded-2xl bg-white border-2 border-emerald-600 p-6" data-testid="seller-rent-range">
          <p className="text-xs uppercase tracking-widest text-emerald-700 font-bold">Si decides arrendar</p>
          <p className="font-serif text-2xl lg:text-3xl font-black mt-1 leading-tight text-habita-dark">
            {fmtCOP(rent.price_min)} <span className="text-gray-400">–</span> {fmtCOP(rent.price_max)}
            <span className="text-base font-normal text-gray-500"> / mes</span>
          </p>
          <p className="text-gray-500 text-sm mt-1" data-testid="seller-rent-admin-note">Canon estimado sin incluir administración.</p>
          {rent.legal_warning && (
            <p className="text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2 mt-3" data-testid="seller-rent-legal-warning">
              El canon estimado de mercado podría superar el límite legal del 1% del valor comercial para vivienda urbana (Ley 820 de 2003). Verifica con un asesor antes de fijar el canon definitivo.
            </p>
          )}
        </div>
      )}

      {/* RENTABILIDAD BRUTA */}
      {yld?.annual_pct != null && (
        <div className="mt-3 rounded-xl bg-habita-mint/60 border border-emerald-200 p-4 flex items-center justify-between" data-testid="seller-gross-yield">
          <div>
            <p className="text-xs uppercase tracking-widest text-emerald-700 font-bold">Rentabilidad bruta estimada</p>
            <p className="text-gray-500 text-xs mt-0.5">canon anual / valor de venta</p>
          </div>
          <p className="font-serif text-2xl font-black text-habita-dark">
            {yld.annual_pct.toLocaleString('es-CO', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%
            <span className="text-sm font-normal text-gray-500"> anual</span>
          </p>
        </div>
      )}

      <p className="text-sm text-gray-600 mt-5">
        El precio final depende también de la vista, el piso y la demanda actual. Da el siguiente paso:
      </p>

      {/* CAPTURA DE VALORACIÓN — solo si aún no está identificado (salvaguarda) */}
      {!identified && (
        <CaptureForm resolveSellerLeadId={async () => result?.lead_id} hasSale={hasSale} hasRent={hasRent} />
      )}


      <div className="mt-4 space-y-3">
        <Link to="/publicar?op=venta&source=valuation" data-testid="seller-publish-sell-cta" className="block"
          onClick={() => { try { track(EventTypes.VALUATION_PUBLISH_SELL_CLICKED, { city: form.city }); } catch { /* noop */ } }}>
          <Button className="w-full bg-habita hover:bg-habita-dark text-white font-bold h-12 rounded-full text-base">
            <HomeIcon className="w-5 h-5 mr-2" /> Publicar para vender
          </Button>
        </Link>
        <Link to="/publicar?op=arriendo&source=valuation" data-testid="seller-publish-rent-cta" className="block"
          onClick={() => { try { track(EventTypes.VALUATION_PUBLISH_RENT_CLICKED, { city: form.city }); } catch { /* noop */ } }}>
          <Button className="w-full bg-white border-2 border-habita text-habita hover:bg-habita-mint font-bold h-12 rounded-full text-base">
            <Building2 className="w-5 h-5 mr-2" /> Publicar para arrendar
          </Button>
        </Link>
        {result?.whatsapp_url ? (
          <a href={result.whatsapp_url} target="_blank" rel="noopener noreferrer" data-testid="seller-whatsapp-cta" className="block">
            <Button className="w-full bg-[#25D366] hover:bg-[#1ebe57] text-white font-bold h-12 rounded-full text-base">
              <MessageCircle className="w-5 h-5 mr-2" /> Contactar a Cristian por WhatsApp
            </Button>
          </a>
        ) : (
          <p data-testid="seller-whatsapp-unavailable" className="text-sm text-amber-700 bg-amber-50 rounded-lg px-3 py-2">
            Un asesor Habita te contactará muy pronto.
          </p>
        )}
        <button onClick={onRestart} data-testid="seller-lead-reset" className="w-full text-center text-sm text-gray-500 hover:text-habita-dark mt-1 underline">
          Valorar otro inmueble
        </button>
      </div>
    </div>
  );
}

/* ---------- Captura de valoración: reporte detallado ---------- */
function CaptureForm({ resolveSellerLeadId, hasSale, hasRent, submitLabel, onCaptured }) {
  const { user } = useAuth();
  const [values, setValues] = useState({ name: '', email: '', phone: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const set = (k, v) => setValues((s) => ({ ...s, [k]: v }));
  const valid =
    values.name.trim().length >= 2 &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email.trim()) &&
    values.phone.trim().length >= 7;

  const intent = hasSale && hasRent ? 'ambos' : hasRent ? 'arriendo' : 'venta';

  const submit = async (e) => {
    e?.preventDefault();
    if (!valid || loading) return;
    setError(''); setLoading(true);
    try {
      const sellerLeadId = await resolveSellerLeadId();
      if (!sellerLeadId) throw new Error('No pudimos vincular tu valoración. Intenta de nuevo.');
      await api.valuationCapture({
        seller_lead_id: sellerLeadId,
        name: values.name.trim(),
        email: values.email.trim(),
        phone: values.phone.trim(),
        intent,
        user_id: user?.id || null,
        session_id: getSessionId(),
      });
      setDone(true);
      try { track(EventTypes.LEAD_CREATED, { source: 'valuation', lead_type: 'vendedor', intent }); } catch { /* noop */ }
      onCaptured?.({ lead_id: sellerLeadId, name: values.name.trim(), email: values.email.trim(), phone: values.phone.trim() });
    } catch (err) {
      setError(friendlyError(err));
    } finally { setLoading(false); }
  };

  if (done) {
    return (
      <div className="mt-4 rounded-2xl border-2 border-emerald-600 bg-emerald-50 p-6 text-center" data-testid="capture-success">
        <div className="w-12 h-12 rounded-full bg-emerald-600 flex items-center justify-center mx-auto">
          <CheckCircle2 className="w-7 h-7 text-white" />
        </div>
        <p className="font-serif text-xl font-bold text-habita-dark mt-3">¡Listo! Recibimos tus datos</p>
        <p className="text-sm text-emerald-900 mt-1.5">
          {onCaptured
            ? 'Preparando tu precio exacto y tu reporte…'
            : 'Un asesor de Habita te contactará muy pronto con el reporte detallado de tu inmueble. Sin compromiso.'}
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="mt-4 rounded-2xl border-2 border-habita/30 bg-habita-mint/40 p-6" data-testid="capture-form">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-habita flex items-center justify-center shrink-0">
          <FileText className="w-5 h-5 text-white" />
        </div>
        <div>
          <h3 className="font-serif text-lg font-bold text-habita-dark">¿Quieres recibir el reporte detallado?</h3>
          <p className="text-sm text-gray-600 mt-0.5">
            Déjanos tus datos y un asesor de Habita te enviará el análisis completo y te acompañará, sin compromiso.
          </p>
        </div>
      </div>

      <div className="space-y-3 mt-5">
        <div className="flex items-center border border-gray-200 bg-white rounded-xl px-3.5 h-12 focus-within:border-habita focus-within:ring-2 focus-within:ring-habita/20 transition">
          <User className="w-4 h-4 text-habita mr-2 shrink-0" />
          <input data-testid="capture-name-input" type="text" value={values.name}
            onChange={(e) => set('name', e.target.value)} placeholder="Nombre completo"
            className="w-full outline-none text-habita-dark placeholder:text-gray-400 bg-transparent" />
        </div>
        <div className="flex items-center border border-gray-200 bg-white rounded-xl px-3.5 h-12 focus-within:border-habita focus-within:ring-2 focus-within:ring-habita/20 transition">
          <Mail className="w-4 h-4 text-habita mr-2 shrink-0" />
          <input data-testid="capture-email-input" type="email" value={values.email}
            onChange={(e) => set('email', e.target.value)} placeholder="Correo electrónico"
            className="w-full outline-none text-habita-dark placeholder:text-gray-400 bg-transparent" />
        </div>
        <div className="flex items-center border border-gray-200 bg-white rounded-xl px-3.5 h-12 focus-within:border-habita focus-within:ring-2 focus-within:ring-habita/20 transition">
          <Phone className="w-4 h-4 text-habita mr-2 shrink-0" />
          <input data-testid="capture-phone-input" type="tel" value={values.phone}
            onChange={(e) => set('phone', e.target.value)} placeholder="Teléfono / WhatsApp"
            className="w-full outline-none text-habita-dark placeholder:text-gray-400 bg-transparent" />
        </div>
      </div>

      {error && <p data-testid="capture-error" className="text-sm text-red-600 mt-3 bg-red-50 rounded-lg px-3 py-2">{error}</p>}

      <Button type="submit" disabled={!valid || loading} data-testid="capture-submit"
        className="w-full mt-4 bg-habita hover:bg-habita-dark text-white font-bold h-12 rounded-full text-base disabled:opacity-50">
        {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Enviando...</> : <>{submitLabel || 'Recibir mi reporte detallado'} <ArrowRight className="w-4 h-4 ml-2" /></>}
      </Button>
      <p className="text-xs text-gray-400 text-center mt-2">Usaremos tus datos solo para enviarte el reporte y contactarte. Puedes solicitar su eliminación cuando quieras.</p>
    </form>
  );
}

/* ---------- small UI helpers ---------- */
function Label({ icon: Icon, children }) {
  return (
    <label className="text-sm font-semibold text-habita-dark mb-1.5 flex items-center gap-2">
      <Icon className="w-4 h-4 text-habita" /> {children}
    </label>
  );
}
function Field({ icon: Icon, label, children }) {
  return (
    <div>
      <Label icon={Icon}>{label}</Label>
      <div className="flex items-center border border-gray-200 rounded-xl px-3.5 h-12 focus-within:border-habita focus-within:ring-2 focus-within:ring-habita/20 transition">
        {children}
      </div>
    </div>
  );
}
function StepBadge({ n, total, label, done }) {
  return (
    <div className="flex items-center gap-2">
      <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${done ? 'bg-emerald-600 text-white' : 'bg-habita-mint text-habita-dark'}`}>
        {done ? <CheckCircle2 className="w-4 h-4" /> : n}
      </span>
      <span className="text-xs uppercase tracking-widest text-gray-400 font-bold">Paso {n} de {total} · {label}</span>
    </div>
  );
}
function QBlock({ title, children }) {
  return (
    <div>
      <h2 className="font-serif text-xl font-bold text-habita-dark mb-4">{title}</h2>
      {children}
    </div>
  );
}
function Stepper({ label, icon: Icon, value, onChange, min, max, testid }) {
  return (
    <div className="flex items-center justify-between border border-gray-200 rounded-xl px-4 h-14 mb-3">
      <div className="flex items-center gap-2 text-habita-dark font-semibold text-sm">
        <Icon className="w-5 h-5 text-habita" /> {label}
      </div>
      <div className="flex items-center gap-4">
        <button type="button" onClick={() => onChange(Math.max(min, value - 1))} data-testid={`${testid}-minus`}
          className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center text-habita-dark hover:bg-habita-mint disabled:opacity-40" disabled={value <= min}>
          <Minus className="w-4 h-4" />
        </button>
        <span className="w-6 text-center font-bold text-habita-dark" data-testid={`${testid}-value`}>{value}</span>
        <button type="button" onClick={() => onChange(Math.min(max, value + 1))} data-testid={`${testid}-plus`}
          className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center text-habita-dark hover:bg-habita-mint disabled:opacity-40" disabled={value >= max}>
          <Plus className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
